import { useContext } from 'react';
import { useGT } from 'gt-react';
import {
  SingleSelect,
  type SingleSelectOption,
} from '@/components/common/SingleSelect';
import { useSops } from 'src/api/rpc/hooks/agent/useOperatorSops';
import { AuthContext } from 'src/context/auth.context';
import {
  sopKey,
  sopLabel,
} from 'src/pages/authenticated/operator/procedureDisplay';

/** Searchable single-procedure picker over the org's SOPs. Shared by the agent task-management
 * lifecycle phases and the browser workflow node's procedure-runner mode. */
export function SopSelect({
  value,
  onChange,
  disabled = false,
  id,
}: {
  value: string;
  onChange: (sopId: string) => void;
  /** Viewer mode — the picker shows its current value but can't be changed. */
  disabled?: boolean;
  /** Lands on the trigger button so a caller's `<label htmlFor>` names this picker. Without it the
   * control is announced by its own contents alone ("Choose a procedure"), losing whatever the
   * surrounding label added — e.g. that this is the OPTIONAL LOGIN procedure. */
  id?: string;
}) {
  const gt = useGT();
  const { currentUser } = useContext(AuthContext);
  const { sops, isLoading } = useSops({
    organizationId: currentUser?.organizationId,
  });
  const options: SingleSelectOption[] = sops.map((s) => ({
    id: s.id,
    label: sopLabel(s),
    // Titles aren't unique (an imported copy keeps its title); the kebab name tells them apart.
    secondaryLabel: sopKey(s) ?? undefined,
  }));
  const selectedOption = options.find((o) => o.id === value) ?? null;

  return (
    <SingleSelect
      id={id}
      options={options}
      value={selectedOption}
      onChange={(option) => onChange(option?.id ?? '')}
      placeholder={
        isLoading
          ? gt('Loading procedures...', {
              context: 'procedure phase select loading placeholder',
            })
          : gt('Choose a procedure', {
              context: 'procedure phase select placeholder',
            })
      }
      searchPlaceholder={gt('Search procedures...', {
        context: 'procedure phase select search placeholder',
      })}
      disabled={isLoading || disabled}
      fullWidth
      allowClear
    />
  );
}
